import { Controller } from "@hotwired/stimulus"

const DEFAULT_ERROR_MESSAGE = "Evercooked could not update your cook profile visibility. This will stay private for now."

export default class extends Controller {
  static targets = [ "dialog", "confirmButton", "error", "publicRadio", "privateRadio", "visibilityHelper" ]
  static values = {
    profilePublic: Boolean,
    resourceNoun: String,
    updateUrl: String
  }

  connect() {
    this.previousActiveElement = null
    this.pendingFocusFrame = null
    this.boundResetForTurboCache = this.resetForTurboCache.bind(this)
    document.addEventListener("turbo:before-cache", this.boundResetForTurboCache)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.boundResetForTurboCache)
    this.resetForTurboCache()
  }

  open(event) {
    if (this.profilePublicValue) {
      this.selectPublic()
      return
    }

    event.preventDefault()
    this.selectPrivate()
    this.previousActiveElement = this.inlineVisibilityTriggerFor(event.currentTarget) || event.currentTarget
    this.clearError()
    this.showDialog()
  }

  async confirm() {
    if (this.profilePublicValue) {
      this.selectPublic()
      this.hideDialog()
      this.dispatch("confirmed", { detail: { profilePublic: true } })
      return
    }

    this.clearError()
    this.confirmButtonTarget.disabled = true

    try {
      const response = await fetch(this.updateUrlValue, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: this.requestBody()
      })

      const payload = await this.parseJson(response)

      if (!response.ok) {
        throw new Error(payload.errors?.[0] || DEFAULT_ERROR_MESSAGE)
      }

      this.profilePublicValue = payload.profile_public ?? true
      this.selectPublic()
      this.hideDialog()
      this.clearError()
      this.dispatch("confirmed", { detail: { profilePublic: this.profilePublicValue } })
    } catch (error) {
      this.selectPrivate()
      this.showError(error.message || this.defaultErrorMessage())
    } finally {
      this.confirmButtonTarget.disabled = false
    }
  }

  cancel(event) {
    if (event) event.preventDefault()

    this.selectPrivate()
    this.hideDialog()
    this.dispatch("cancelled")
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  selectPublic() {
    if (this.hasPublicRadioTarget) this.publicRadioTarget.checked = true
    if (this.hasPrivateRadioTarget) this.privateRadioTarget.checked = false
    this.updateVisibilityHelper("Public")
  }

  selectPrivate() {
    if (this.hasPrivateRadioTarget) this.privateRadioTarget.checked = true
    if (this.hasPublicRadioTarget) this.publicRadioTarget.checked = false
    this.updateVisibilityHelper("Private")
  }

  updateVisibilityHelper(visibility) {
    if (!this.hasVisibilityHelperTarget) return

    this.visibilityHelperTarget.textContent = `Photos are optional. This preparation will start ${visibility}.`
  }

  showDialog() {
    if (!this.hasDialogTarget) return

    this.cancelPendingFocus()
    this.dialogTarget.classList.remove("hidden")
    this.dialogTarget.setAttribute("aria-hidden", "false")
    this.pendingFocusFrame = requestAnimationFrame(() => {
      this.pendingFocusFrame = null
      if (this.dialogTarget.classList.contains("hidden") || !this.hasConfirmButtonTarget) return

      this.confirmButtonTarget.focus()
    })
  }

  hideDialog() {
    this.cancelPendingFocus()
    if (!this.hasDialogTarget) return

    this.dialogTarget.classList.add("hidden")
    this.dialogTarget.setAttribute("aria-hidden", "true")
    this.restoreFocus()
  }

  resetForTurboCache() {
    this.cancelPendingFocus()
    const dialogWasOpen = this.hasDialogTarget && !this.dialogTarget.classList.contains("hidden")

    if (dialogWasOpen) this.selectPrivate()

    if (this.hasDialogTarget) {
      this.dialogTarget.classList.add("hidden")
      this.dialogTarget.setAttribute("aria-hidden", "true")
      this.blurDialogFocus()
    }

    if (this.hasConfirmButtonTarget) this.confirmButtonTarget.disabled = false
    this.clearError()
    this.previousActiveElement = null
  }

  cancelPendingFocus() {
    if (this.pendingFocusFrame === null) return

    cancelAnimationFrame(this.pendingFocusFrame)
    this.pendingFocusFrame = null
  }

  blurDialogFocus() {
    if (!this.hasDialogTarget || !this.dialogTarget.contains(document.activeElement)) return

    document.activeElement.blur()
  }

  restoreFocus() {
    if (this.previousActiveElement && document.contains(this.previousActiveElement)) {
      this.previousActiveElement.focus()
      return
    }

    if (this.hasPublicRadioTarget) {
      this.publicRadioTarget.focus()
    }
  }

  clearError() {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }

  showError(message) {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  requestBody() {
    const formData = new FormData()
    formData.append("user[profile_public]", "true")
    return formData
  }

  defaultErrorMessage() {
    return `Evercooked could not update your cook profile visibility. Your ${this.resourceNounValue} is still private.`
  }

  inlineVisibilityTriggerFor(element) {
    return element
      ?.closest("[data-controller~='inline-visibility']")
      ?.querySelector("[data-inline-visibility-target='trigger']")
  }

  async parseJson(response) {
    try {
      return await response.json()
    } catch {
      return {}
    }
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }
}
